import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert, assertAndContinue} from 'Asserts';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {
  displayParentLineagePath,
  getSourceParentSchema,
  getTargetParentSchema,
  pathWithoutParentLinkEdges,
} from 'registry/child-entities/ParentLineagePathUtil';
import {
  constructPath,
  displayPath,
  EdgeDirection,
  getRequiredLinkEntitySchemaFieldPath,
} from 'registry/field/FieldPathUtil';
import {SchemaField, SchemaFieldEdge} from 'schemas/fields/SchemaFieldConstants';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GREEN_LIGHT} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';

const NO_PATH_TEXT = 'No lineage enforcement';

interface Props {
  field: SchemaField;
  availablePaths: Array<Array<SchemaFieldEdge>>;
  sourceSchema: TagSchemaSummary | TagSchemaSummaryGraphQL;
  targetSchema: TagSchemaSummary | TagSchemaSummaryGraphQL;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

enum EnforcementType {
  LINEAR = 'LINEAR',
  SHARED_TARGET = 'SHARED_TARGET',
  SHARED_SOURCE = 'SHARED_SOURCE',
}

function PathExplanation(props: Props & {fullSelectedPath: Array<SchemaFieldEdge>}): JSX.Element {
  const pathToDisplay = pathWithoutParentLinkEdges(props.fullSelectedPath);
  const sourceParentSchema = getSourceParentSchema(props.fullSelectedPath);
  const targetParentSchema = getTargetParentSchema(props.fullSelectedPath);

  const inferEnforcementType = (): EnforcementType => {
    if (pathToDisplay.length === 1) {
      return EnforcementType.LINEAR;
    }
    assert(
      pathToDisplay.length === 2,
      'Expected path to display for requiredLinkEntitySchemaFieldPath to have 1 or 2 edges'
    );
    const pathWithDirections = constructPath(sourceParentSchema.id, pathToDisplay);
    const edgeDirections = pathWithDirections.path.map((edge) => edge.direction);
    // constructPath adds an extra first edge with a 'direction' of null
    if (edgeDirections[1] === edgeDirections[2]) {
      return EnforcementType.LINEAR;
    }
    if (edgeDirections[1] === EdgeDirection.OUT) {
      return EnforcementType.SHARED_TARGET;
    }
    return EnforcementType.SHARED_SOURCE;
  };

  const getPathExplanation = (): JSX.Element => {
    const enforcementType = inferEnforcementType();
    const sourceChildSchemaName = props.sourceSchema.name;
    const targetChildSchemaName = props.targetSchema.name;
    const sourceParentSchemaName = sourceParentSchema.name;
    const targetParentSchemaName = targetParentSchema.name;
    const pathDisplayText = displayPath({
      sourceTagSchemaId: sourceParentSchema.id,
      tagSchemaFieldEdges: pathToDisplay,
      includeFields: true,
    });
    if (enforcementType === EnforcementType.LINEAR) {
      return (
        <div {...getTestProps({component: 'PathExplanation', key: 'linearLineageExplanation'})}>
          Enforce that the linked <b>{targetChildSchemaName}</b> entity must be a child of the{' '}
          <b>{targetParentSchemaName}</b> entity that <b>{sourceChildSchemaName}</b>‘s parent{' '}
          <b>{sourceParentSchemaName}</b> links to via the schema path <b>{pathDisplayText}</b>.
        </div>
      );
    } else if (enforcementType === EnforcementType.SHARED_TARGET) {
      const sharedTargetSchemaName = pathToDisplay[0].to!.name;
      return (
        <div {...getTestProps({component: 'PathExplanation', key: 'sharedTargetExplanation'})}>
          Enforce that the linked <b>{targetChildSchemaName}</b> entity must be a child of a{' '}
          <b>{targetParentSchemaName}</b> entity which links to the same <b>{sharedTargetSchemaName}</b>{' '}
          entity that <b>{sourceChildSchemaName}</b>’s parent <b>{sourceParentSchemaName}</b> also links to.
        </div>
      );
    } else {
      const sharedSourceSchemaName = pathToDisplay[0].from!.name;
      return (
        <div {...getTestProps({component: 'PathExplanation', key: 'sharedSourceExplanation'})}>
          Enforce that the linked <b>{targetChildSchemaName}</b> entity must be a child of a{' '}
          <b>{targetParentSchemaName}</b> entity which is linked from the same <b>{sharedSourceSchemaName}</b>{' '}
          entity that <b>{sourceChildSchemaName}</b>’s parent <b>{sourceParentSchemaName}</b> is also linked
          from.
        </div>
      );
    }
  };
  return (
    <div className={css(styles.pathExplanationContainer)}>
      <BenchlingIcon className={css(styles.checkIcon)} icon="check" />
      <div className={css(styles.modalText, styles.pathExplanationText)}>{getPathExplanation()}</div>
    </div>
  );
}

function PathSelector(
  props: Props & {selectedPathIndex: number; setSelectedPathIndex: (index: number) => void}
): JSX.Element {
  const optionTexts = getPathOptionTexts(props);
  const options: Array<JSX.Element> = optionTexts.map((optionText, index) => (
    <Option
      key={index}
      value={optionText}
      onSelect={() => {
        props.setSelectedPathIndex(index);
      }}
    >
      {optionText}
    </Option>
  ));
  options.push(
    <Option
      key="no-path-option"
      value={null}
      onSelect={() => {
        props.setSelectedPathIndex(-1);
      }}
    >
      {NO_PATH_TEXT}
    </Option>
  );
  return (
    <SelectDropdown
      menu={
        <Menu width={Menu.WIDTHS.FULL} value={props.selectedPathIndex} className={css(styles.modalText)}>
          {options}
        </Menu>
      }
      displayName={props.selectedPathIndex === -1 ? NO_PATH_TEXT : optionTexts[props.selectedPathIndex]}
    />
  );
}

function getPathOptionTexts(props: Props): Array<string> {
  const pathInfos = props.availablePaths.map((path) => ({
    path,
    displayPath: displayParentLineagePath({path}),
  }));
  const freq = new Map();
  pathInfos.forEach((pathInfo) => {
    const count = freq.get(pathInfo.displayPath) ?? 0;
    freq.set(pathInfo.displayPath, count + 1);
  });
  const ambiguousPaths = pathInfos.filter((pathInfo) => freq.get(pathInfo.displayPath) > 1);
  return pathInfos.map((pathInfo) =>
    ambiguousPaths.includes(pathInfo)
      ? displayParentLineagePath({path: pathInfo.path, includeFields: true})
      : pathInfo.displayPath
  );
}

export default function ConfigureEntityLineagePathModal(props: Props): JSX.Element {
  const getInitialPathIndex = (): number => {
    let pathIndex = -1;
    const currentlySetPath = getRequiredLinkEntitySchemaFieldPath(props.field);
    if (currentlySetPath) {
      for (let i = 0; i < props.availablePaths.length; i++) {
        if (isEqual(props.availablePaths[i], currentlySetPath.edges)) {
          pathIndex = i;
          break;
        }
      }
    }
    assertAndContinue(
      currentlySetPath == null || pathIndex !== -1,
      `Currently-set entitySchemaFieldPath did not match any available path options: field_id=${props.field.apiId}`
    );
    return pathIndex;
  };

  const [selectedPathIndex, setSelectedPathIndex] = useState(getInitialPathIndex());

  const handleSubmit = async (): Promise<void> => {
    await withLoadingScreen(
      TagSchemaActions.updatedRequiredLinkEntitySchemaFieldPath(
        props.field.apiId,
        selectedPathIndex === -1 ? null : props.availablePaths[selectedPathIndex]
      )
    );
    props.onRequestSubmit();
  };

  return (
    <Modal
      title="Enforce Parent Lineage"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: 'Save',
          // Disable submission if selection is un-changed
          disabled: selectedPathIndex === getInitialPathIndex(),
          submit: true,
        },
      ]}
    >
      <div className={css(styles.modalText, styles.introTextPadding)}>
        Select a path of link fields that will be required to exist between parent entities in order for
        entity link <b>{props.field.name}</b> to be valid.
      </div>
      <PathSelector
        {...props}
        selectedPathIndex={selectedPathIndex}
        setSelectedPathIndex={setSelectedPathIndex}
      />
      {selectedPathIndex !== -1 && (
        <PathExplanation {...props} fullSelectedPath={props.availablePaths[selectedPathIndex]} />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalText: {
    fontSize: '14px',
  },
  introTextPadding: {
    paddingBottom: '12px',
  },
  pathExplanationContainer: {
    padding: '12px',
    paddingLeft: '0px',
    paddingRight: '22px',
    display: 'flex',
  },
  pathExplanationText: {
    flex: 11,
  },
  checkIcon: {
    flex: 1,
    color: GREEN_LIGHT,
    marginTop: '4px',
    height: '14px',
  },
});
